<template>
  <div class="step1-basic-info">
    <el-form
      ref="step1Form"
      :model="formData"
      :rules="rules"
      label-width="120px"
      style="width: 700px; margin: 0 auto;"
    >
      <el-form-item label="任务组名称" prop="groupName">
        <el-input v-model="formData.groupName" placeholder="根据所选配置自动生成，可手动修改"/>
      </el-form-item>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="任务类型" prop="taskType">
            <el-select
              v-model="formData.taskType"
              style="width: 100%"
              @change="$emit('auto-name')"
            >
              <el-option
                v-for="opt in taskTypeOptions"
                :key="opt.value"
                :label="opt.label"
                :value="opt.value"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="执行周期" prop="cycleType">
            <el-select
              v-model="formData.cycleType"
              style="width: 100%"
              @change="$emit('cycle-type-change')"
            >
              <el-option
                v-for="opt in cycleTypeOptions"
                :key="opt.value"
                :label="opt.label"
                :value="opt.value"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="执行大区" prop="zoneAreaId">
            <AreaCascader
              v-model="formData.zoneAreaId"
              :options="rootAreaOptions"
              placeholder="请选择园区/场馆"
              style="width: 100%"
              clearable
              @change="$emit('zone-change')"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item v-if="showTaskYearField" label="任务年份" prop="taskYear">
            <el-select
              v-model="formData.taskYear"
              style="width: 100%"
              placeholder="请选择或输入年份"
              filterable
              allow-create
              default-first-option
              @change="$emit('task-year-change')"
            >
              <el-option
                v-for="y in yearOptions"
                :key="y"
                :label="`${y}年`"
                :value="y"
              />
            </el-select>
          </el-form-item>
          <el-form-item v-else />
        </el-col>
      </el-row>

      <!-- 人员分配区块 -->
      <AssignTypeFields
        :model="formData"
        team-type="task"
        preset="task"
        divider-title="默认执行人员分配（可选）"
        :active="panelActive"
        @navigate-to-team="$emit('navigate-to-team')"
        @navigate-to-org-user="$emit('navigate-to-org-user')"
      />
    </el-form>
  </div>
</template>

<script>
/**
 * 第一步：任务组基本信息（名称、类型、周期、大区、年份、默认执行分配）
 * 仅负责展示与校验，变更事件通过 emit 通知父组件处理联动逻辑。
 */

// 工具函数
import {
  TASK_TYPE_OPTIONS,
  CYCLE_TYPE_OPTIONS,
  isCycleWithoutTaskYear
} from '@/features/gnmk/constants/taskCycleEnums';
// 组件
import AssignTypeFields from '@/features/gnmk/components/AssignTypeFields.vue';
import AreaCascader from '@/features/gnmk/components/AreaCascader.vue';

export default {
  name: 'Step1BasicInfo',
  components: {
    AssignTypeFields,
    AreaCascader
  },
  props: {
    /** 与父组件共用的表单模型 */
    formData: {
      type: Object,
      required: true
    },
    /** 区域树（大区级联数据源） */
    areaTree: {
      type: Array,
      default: () => []
    },
    /** 弹窗是否打开：用于分配区块每次打开时刷新小组/人员选项 */
    panelActive: {
      type: Boolean,
      default: true
    }
  },
  data () {
    const currentYear = new Date().getFullYear();
    // 年份下拉选项
    const yearOptions = Array.from({ length: 11 }, (_, i) => currentYear + i);
    return {
      taskTypeOptions: TASK_TYPE_OPTIONS,
      cycleTypeOptions: CYCLE_TYPE_OPTIONS,
      yearOptions,
      rules: {
        groupName: [{ required: true, message: '必填', trigger: 'blur' }],
        taskType: [{ required: true, message: '必选', trigger: 'change' }],
        cycleType: [{ required: true, message: '必选', trigger: 'change' }],
        zoneAreaId: [{ required: true, message: '必选', trigger: 'change' }],
        taskYear: [
          {
            validator: (rule, value, callback) => {
              if (isCycleWithoutTaskYear(this.formData.cycleType)) {
                callback();
              } else if (value === null || value === undefined || value === '') {
                callback(new Error('必选'));
              } else {
                callback();
              }
            },
            trigger: 'change'
          }
        ]
      }
    };
  },
  computed: {
    /** 级联只展示根节点一层，避免一次性展开整棵树 */
    rootAreaOptions () {
      return this.areaTree.map(node => ({
        ...node,
        children: null
      }));
    },
    /** 日常/专项不选任务年份，其余周期需选年份 */
    showTaskYearField () {
      return !isCycleWithoutTaskYear(this.formData.cycleType);
    }
  },
  methods: {
    /** 供父组件 ref 调用，返回第一步表单是否通过校验 */
    validate () {
      return new Promise((resolve) => {
        this.$refs.step1Form.validate((valid) => {
          resolve(valid);
        });
      });
    }
  }
};
</script>

<style scoped lang="scss">
/* 修复必填提示被遮挡 */
::v-deep .el-form-item__error {
  position: relative;
  top: 0;
  padding-top: 2px;
}
::v-deep .el-form-item {
  margin-bottom: 18px;
}
</style>
